<template>
  <div class="feature-flag-toggle">
    <label class="toggle-switch" :class="{ 'is-disabled': disabled || loading }">
      <input 
        type="checkbox" 
        :checked="effectiveValue" 
        :disabled="disabled || loading"
        @change="handleToggle"
      />
      <span class="toggle-slider" :class="{ 'is-loading': loading }">
        <span v-if="loading" class="toggle-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
      </span>
    </label>
  </div>
</template>

<script>
/**
 * FeatureFlagToggle Component
 * 
 * Toggle switch component for feature flags in Extranet.
 * Provides smooth animation and clear visual states.
 * 
 * @component
 * @example
 * <feature-flag-toggle 
 *   :flag="{ id: 1, key: 'feature_x', is_enabled: true }"
 *   :disabled="false"
 *   :loading="false"
 *   @toggle="handleToggle"
 * />
 */
export default {
  name: 'FeatureFlagToggle',

  props: {
    /**
     * Feature flag object
     * @type {Object}
     * @property {number} id - Flag ID
     * @property {string} key - Flag key
     * @property {boolean} is_enabled - Current enabled state
     */
    flag: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value.id !== undefined &&
          (value.key || value.name) &&
          typeof value.is_enabled === 'boolean'
        )
      }
    },

    /**
     * Disable the toggle switch
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Show loading state
     */
    loading: {
      type: Boolean,
      default: false
    },

    /**
     * Override the displayed checked state (for pending local changes).
     * When null, falls back to flag.is_enabled.
     */
    overrideValue: {
      type: Boolean,
      default: null
    }
  },

  emits: ['toggle'],

  computed: {
    effectiveValue() {
      return this.overrideValue !== null ? this.overrideValue : this.flag.is_enabled
    }
  },

  methods: {
    /**
     * Handle toggle change event
     * Emit toggle event with flag ID and new value
     */
    handleToggle(event) {
      if (this.disabled || this.loading) {
        return
      }

      const newValue = event.target.checked
      this.$emit('toggle', this.flag.id, newValue)
    }
  }
}
</script>

<style scoped>
/* Toggle Container */
.feature-flag-toggle {
  display: inline-block;
  position: relative;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  cursor: pointer;
}

.toggle-switch.is-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Hide default checkbox */
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Toggle Slider */
.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  border-radius: 26px;
  transition: all 0.3s ease;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Checked state */
input:checked + .toggle-slider {
  background-color: var(--extranet-primary, #fc6666);
}

input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

/* Focus state */
input:focus + .toggle-slider {
  box-shadow: 0 0 0 3px rgba(252, 102, 102, 0.2);
}

/* Hover state */
.toggle-switch:not(.is-disabled):hover .toggle-slider {
  background-color: #a0aec0;
}

.toggle-switch:not(.is-disabled):hover input:checked + .toggle-slider {
  background-color: var(--extranet-accent, #fc6666);
  opacity: 0.9;
}

/* Loading state */
.toggle-slider.is-loading {
  background-color: #e2e8f0;
}

input:checked + .toggle-slider.is-loading {
  background-color: var(--extranet-primary, #fc6666);
  opacity: 0.7;
}

.toggle-slider.is-loading:before {
  opacity: 0.6;
}

/* Loading spinner */
.toggle-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #4a5568;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

input:checked + .toggle-slider .toggle-spinner {
  color: white;
}

/* Disabled state */
.toggle-switch.is-disabled .toggle-slider {
  background-color: #e2e8f0;
}

.toggle-switch.is-disabled input:checked + .toggle-slider {
  background-color: var(--extranet-primary, #fc6666);
}
</style>
